import { 
    PUR5010_R01ItemSubtotalData, 
    PUR5010_R01WarehouseSubtotalData, 
    PUR5010_R01BrandSubtotalData, 
    PUR5010_R01ItemSubtotalRow, 
    PUR5010_R01WarehouseSubtotalRow, 
    PUR5010_R01BrandSubtotalRow, 
    PUR5010_R01Dto,
    PUR5010_R01_SUBTOTAL_TYPES,
    PUR5010_R01_SUBTOTAL_LEVELS
} from '../Dto_PUR5010_R01';
import { defaultStockData } from 'src/dtos/stock/stockDto';
import { SUBTOTAL_CONFIGS, PUR5010_R01SubtotalConfig } from './tabConfig';

/**
 * 통합 소계 계산 함수
 */
const PUR5010_R01Subtotals = <T extends any>(
    data: any[], 
    config: PUR5010_R01SubtotalConfig
): T[] => {
    if (!data || data.length === 0) return [];
    
    const grouped = data.reduce((acc, item) => {
        // 그룹화 키 생성
        const key = item[config.groupBy]; // 행의 itemCd 필드로 그룹화

        // 그룹이 처음 등장할 때 초기화
        if (!acc[key]) { 
            // 기본 필드들 초기화
            const baseData: any = {
                [config.groupBy]: item[config.groupBy], // 그룹화 기준 필드 (예: itemCd)
                count: 0,
                groupKey: key
            };
            
            // 표시 displayFields 필드들 초기화 (첫 번째 아이템의 값 사용)
            config.displayFields.forEach(field => {
                baseData[field] = item[field] || '';
            });
            
            // 집계 aggregateFields 필드들 초기화
            config.aggregateFields.forEach(field => {
                baseData[field] = 0;
            });
            
            // 그룹 데이터 초기화
            acc[key] = baseData;
        }
        // 카운트 증가
        acc[key].count += 1;
        
        // 집계 aggregateFields 필드들 누적
        config.aggregateFields.forEach(field => {
            acc[key][field] += Number(item[field]) || 0;
        });
        
        return acc;
    }, {} as Record<string, T>);
    
    // 그룹화된 결과를 배열로 변환하여 반환 (필드 순서 유지)
    return (Object.values(grouped) as T[]).sort((a, b) => 
        (a as any)[config.groupBy].localeCompare((b as any)[config.groupBy])
    );
};

/**
 * 품목별 소계 데이터 계산 (품목별 탭용)
 */
export const PUR5010_R01ItemSubtotals = (data: any[]): PUR5010_R01ItemSubtotalData[] => {
    return PUR5010_R01Subtotals<PUR5010_R01ItemSubtotalData>(data, SUBTOTAL_CONFIGS.item);
};

/**
 * 창고별 소계 데이터 계산 (창고별 탭용)
 */
export const PUR5010_R01WarehouseSubtotals = (data: any[]): PUR5010_R01WarehouseSubtotalData[] => {
    return PUR5010_R01Subtotals<PUR5010_R01WarehouseSubtotalData>(data, SUBTOTAL_CONFIGS.warehouse);
};

/**
 * 브랜드별 소계 데이터 계산 (브랜드별 탭용)
 */
export const PUR5010_R01BrandSubtotals = (data: any[]): PUR5010_R01BrandSubtotalData[] => {
    return PUR5010_R01Subtotals<PUR5010_R01BrandSubtotalData>(data, SUBTOTAL_CONFIGS.brand);
};

/**
 * 품목별 소계 행 생성
 */
export const createItemSubtotalRow = (subtotal: PUR5010_R01ItemSubtotalData): PUR5010_R01ItemSubtotalRow => ({
    ...defaultStockData,
    isSubtotal: true,
    subtotalType: PUR5010_R01_SUBTOTAL_TYPES.ITEM,
    level: PUR5010_R01_SUBTOTAL_LEVELS.ITEM,
    groupKey: subtotal.itemCd,
    groupLabel: `${subtotal.itemCd} 소계 (${subtotal.count}건)`,
    count: subtotal.count,
    itemCd: subtotal.itemCd,
    itemNm: subtotal.itemNm,
    itemSpec: subtotal.itemSpec,
    itemUnit: subtotal.itemUnit,
    stockQty: subtotal.stockQty,
    reservQty: subtotal.reservQty,
    availableQty: subtotal.stockQty // 가용재고는 현재고를 사용
});

/**
 * 창고별 소계 행 생성
 */
export const createWarehouseSubtotalRow = (subtotal: PUR5010_R01WarehouseSubtotalData): PUR5010_R01WarehouseSubtotalRow => {
    const groupKey = subtotal.groupKey || subtotal.itemCd;
    return {
        ...defaultStockData,
        isSubtotal: true,
        subtotalType: PUR5010_R01_SUBTOTAL_TYPES.WAREHOUSE,
        level: PUR5010_R01_SUBTOTAL_LEVELS.WAREHOUSE,
        groupKey: groupKey,
        groupLabel: `${subtotal.itemCd} 소계 (${subtotal.count}건)`,
        count: subtotal.count,
        itemCd: subtotal.itemCd,
        itemNm: subtotal.itemNm,
        itemSpec: subtotal.itemSpec,
        itemUnit: subtotal.itemUnit,
        logicCenter: '', // 소계는 물류센터 구분 없음
        logicCenterNm: '', // 소계는 전체 물류센터
        warehouseCd: '', // 소계는 창고 구분 없음
        warehouseNm: '', // 소계는 전체 창고
        stockQty: subtotal.stockQty,
        reservQty: subtotal.reservQty,
        availableQty: subtotal.stockQty // 가용재고는 현재고를 사용
    };
};

/**
 * 브랜드별 소계 행 생성
 */
export const createBrandSubtotalRow = (subtotal: PUR5010_R01BrandSubtotalData): PUR5010_R01BrandSubtotalRow => ({
    ...defaultStockData,
    isSubtotal: true,
    subtotalType: PUR5010_R01_SUBTOTAL_TYPES.BRAND,
    level: PUR5010_R01_SUBTOTAL_LEVELS.BRAND,
    groupKey: subtotal.groupKey || subtotal.itemCd,
    groupLabel: `${subtotal.itemCd} 소계 (${subtotal.count}건)`,
    count: subtotal.count,
    itemCd: subtotal.itemCd,
    itemNm: subtotal.itemNm,
    itemSpec: subtotal.itemSpec,
    itemUnit: subtotal.itemUnit,
    brandCd: '', // 소계는 브랜드 구분 없음
    brandNm: '', // 소계는 전체 브랜드
    logicCenter: '', // 소계는 물류센터 구분 없음
    logicCenterNm: '', // 소계는 전체 물류센터
    warehouseCd: '', // 소계는 창고 구분 없음
    warehouseNm: '', // 소계는 전체 창고
    stockQty: subtotal.stockQty,
    reservQty: subtotal.reservQty,
    availableQty: subtotal.stockQty // 가용재고는 현재고를 사용
});

/**
 * 상세 행 생성
 */
export const createDetailRow = (item: any): PUR5010_R01Dto => ({
    ...item,
    isSubtotal: false,
    level: PUR5010_R01_SUBTOTAL_LEVELS.DETAIL
});

/**
 * 소계 행 타입 가드 함수들
 */
export const isSubtotalRow = (row: PUR5010_R01Dto): row is PUR5010_R01ItemSubtotalRow | PUR5010_R01WarehouseSubtotalRow | PUR5010_R01BrandSubtotalRow => {
    return row.isSubtotal === true;
};

export const isItemSubtotalRow = (row: PUR5010_R01Dto): row is PUR5010_R01ItemSubtotalRow => {
    return isSubtotalRow(row) && row.subtotalType === PUR5010_R01_SUBTOTAL_TYPES.ITEM;
};

export const isWarehouseSubtotalRow = (row: PUR5010_R01Dto): row is PUR5010_R01WarehouseSubtotalRow => {
    return isSubtotalRow(row) && row.subtotalType === PUR5010_R01_SUBTOTAL_TYPES.WAREHOUSE;
};

export const isBrandSubtotalRow = (row: PUR5010_R01Dto): row is PUR5010_R01BrandSubtotalRow => {
    return isSubtotalRow(row) && row.subtotalType === PUR5010_R01_SUBTOTAL_TYPES.BRAND;
};

/**
 * 데이터와 소계를 병합하는 함수
 */
export const mergeDataWithSubtotals = (
    originalData: any[],
    subtotals: any[],
    showSubtotals: boolean,
    tabType: 'item' | 'warehouse' | 'brand'
): PUR5010_R01Dto[] => {
    // 품목별 탭은 소계 표시하지 않음
    if (tabType === 'item') {
        return originalData.map(createDetailRow);
    }
    
    if (!showSubtotals || !subtotals || subtotals.length === 0) {
        return originalData.map(createDetailRow);
    }

    const result: PUR5010_R01Dto[] = [];
    
    // console.log('소계 데이터 개수:', subtotals.length);
    // console.log('소계 데이터 샘플:', subtotals.slice(0, 3));
    // console.log('소계 데이터 groupKey 확인:', subtotals.slice(0, 3).map(s => ({ itemCd: s.itemCd, groupKey: s.groupKey })));
    
    const subtotalMap = new Map(subtotals.map(s => [s.groupKey || s.itemCd, s]));
    
    // console.log('subtotalMap 키들:', Array.from(subtotalMap.keys()).slice(0, 10));

        // 원본 데이터를 그룹별로 정렬하고 소계 삽입
        const groupedData = originalData.reduce((acc, item) => {
            const groupKey = item.itemCd; // 품목코드만으로 그룹화

        if (!acc[groupKey]) {
            acc[groupKey] = [];
        }
        acc[groupKey].push(item);
        return acc;
    }, {} as Record<string, any[]>);
    
    // console.log('그룹 키들 (샘플):', Object.keys(groupedData).slice(0, 10));

    // 각 그룹별로 상세 행 먼저 추가, 그 다음 소계 행 추가
    Object.keys(groupedData).sort().forEach(groupKey => {
        const subtotal = subtotalMap.get(groupKey);
        // console.log(`그룹 키: ${groupKey}, 소계 데이터 찾기:`, subtotal ? '찾음' : '없음');
        
        // 상세 행들 먼저 추가
        groupedData[groupKey].forEach((item: any) => {
            result.push(createDetailRow(item));
        });
        
        // 그 다음 소계 행 추가
        if (subtotal) {
            if (tabType === 'warehouse') {
                const subtotalRow = createWarehouseSubtotalRow(subtotal);
                // console.log('창고별 소계 행 생성:', subtotalRow);
                result.push(subtotalRow);
            } else if (tabType === 'brand') {
                const subtotalRow = createBrandSubtotalRow(subtotal);
                // console.log('브랜드별 소계 행 생성:', subtotalRow);
                result.push(subtotalRow);
            }
        }
    });
    
    // console.log('최종 병합 결과 - 소계 행 개수:', result.filter(row => isSubtotalRow(row)).length);
    // console.log('최종 병합 결과 - 상세 행 개수:', result.filter(row => !isSubtotalRow(row)).length);

    return result;
};

export const SUBTOTAL_ROW_CLASS_RULES = {
    'subtotal-row': (params: any) => isSubtotalRow(params.data),
};
